import { useStore } from '@nanostores/react';
import { useCallback, useEffect, useState, useRef } from 'react';
import { InactiveSubscriptionBanner } from './InactiveSubscriptionBanner';
import { PaymentFailBanner } from './PaymentFailBanner';
import { TeamMembersHeader } from './TeamMembersHeader';
import { TeamSettings } from './TeamSettings';
import { TeamTokensInfo } from './TeamTokensInfo';
import { SectionContainer, SectionRow } from '~/components/dialogs/settings/SectionContainer';
import { Button } from '~/components/ui/Button';
import { MembersTable } from '~/components/ui/MembersTable/MembersTable';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { organizationMembersStore } from '~/lib/stores/organizationMembers';
import { teamStore } from '~/lib/stores/team';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import type { OrganizationData } from '~/types/organization';
import { teamsPricingList } from '~/utils/pricing';
import { redirectToTeamsMembersPage } from '~/utils/subscription';

interface MemberTeamScreenProps {
  activeOrganization: OrganizationData;
  showPricingOverlay: VoidFunction;
}

export const MemberTeamScreen = ({ activeOrganization, showPricingOverlay }: MemberTeamScreenProps) => {
  const activeOrgMembers = useStore(organizationMembersStore.activeOrgMembers);
  const user = useStore(userStore.user);
  const hasSentAnalyticsRef = useRef(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    teamStore.isLoadingTeam.set(true);
    organizationMembersStore.fetchMembers(activeOrganization, true).finally(() => {
      teamStore.isLoadingTeam.set(false);
    });
  }, []);

  useEffect(() => {
    teamStore.members.set(activeOrgMembers);

    if (!hasSentAnalyticsRef.current && activeOrgMembers.length > 0) {
      hasSentAnalyticsRef.current = true;
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.TeamSettingsModalOpened,
          properties: {
            organization_id: activeOrganization.id,
            members: activeOrgMembers.length,
          },
        },
      });
    }
  }, [activeOrgMembers]);

  const handleCloseSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  const handleOpenSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  const handleRefetchMembers = useCallback(() => {
    organizationMembersStore.fetchMembers(activeOrganization, true);
  }, [activeOrganization, organizationMembersStore]);

  const subscription = activeOrganization?.billing?.subscription;
  const membership = teamsPricingList.find((m) => m.tier === activeOrganization?.billing?.tier);
  const interval = subscription?.interval;
  const isAdmin = activeOrganization?.role === 'admin';

  if (showSettings && isAdmin) {
    return <TeamSettings closeTeamSettings={handleCloseSettings} />;
  }

  return (
    <SectionContainer
      title={
        <TeamMembersHeader
          showTeamSettings={handleOpenSettings}
          activeOrganization={activeOrganization}
          showPricingOverlay={showPricingOverlay}
        />
      }
    >
      {isAdmin && (
        <>
          {subscription?.status === 'past_due' && <PaymentFailBanner showPricingOverlay={showPricingOverlay} />}

          {subscription?.status === 'canceled' && (
            <InactiveSubscriptionBanner showPricingOverlay={showPricingOverlay} />
          )}

          {membership && (
            <p className="text-base font-medium">
              {membership.name} {membership.namePrice} {interval === 'yearly' ? 'annual' : 'monthly'} billing
            </p>
          )}

          <TeamTokensInfo activeOrganization={activeOrganization} />

          {!user?.featureFlags.teamManagement && (
            <SectionRow
              title={
                <div className="max-w-72">
                  Manage your team on StackBlitz: add or delete members, change roles, send invites
                </div>
              }
              action={
                <Button onClick={() => redirectToTeamsMembersPage(activeOrganization)} className="shrink-0">
                  Manage team
                </Button>
              }
            />
          )}
        </>
      )}

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-medium">Members</h4>
        </div>

        <MembersTable
          refetchMembers={handleRefetchMembers}
          organization={activeOrganization}
          type={isAdmin ? 'admin-view' : 'member-view'}
        />
      </div>
    </SectionContainer>
  );
};
